// Channel table (doc 03 §3.4) — the heart of the origin integration, plus the
// bulk URL rewrite that doubles as restreamer activation (doc 01 §6).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type Channel, type Me, type Page } from "../api";
import {
  Badge,
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  Modal,
  PageHeader,
  Spinner,
  StatusDot,
  useAction,
} from "../ui";

export default function Channels({ me }: { me: Me }) {
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Channel | "new" | null>(null);
  const [rewriting, setRewriting] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<Page<Channel>>({
    queryKey: ["channels"],
    queryFn: () => api.get("/v1/channels"),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["channels"] });
  const rows = (data?.items ?? []).filter(
    (c) => !query || c.name.toLowerCase().includes(query.toLowerCase()) || String(c.number).includes(query),
  );

  return (
    <>
      <PageHeader
        title="Channel table"
        subtitle="Each channel maps to a profile-specific playlist on a primary and a backup DVR."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setRewriting(true)}>
              Bulk URL rewrite
            </button>
            <button className="btn-primary" onClick={() => setEditing("new")}>
              New channel
            </button>
          </>
        }
      />

      <input
        className="input mb-4 max-w-xs"
        placeholder="Search channels…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <Card padded={false}>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4">
            <ErrorNote error={error} />
          </div>
        ) : !rows.length ? (
          <Empty>No channels in the table yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Channel</th>
                  <th>hd origin</th>
                  <th>sd origin</th>
                  <th>Nominal kbps</th>
                  <th>SCTE-35</th>
                  <th>Status</th>
                  <th>Outposts</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((channel) => (
                  <tr key={channel.id}>
                    <td className="tabular-nums font-medium">{channel.number}</td>
                    <td>{channel.name}</td>
                    <td>
                      <Origins urls={channel.urls_json.hd} />
                    </td>
                    <td>
                      <Origins urls={channel.urls_json.sd} />
                    </td>
                    <td className="tabular-nums text-xs text-ink-300">
                      hd {channel.nominal_kbps_json.hd ?? "—"} · sd {channel.nominal_kbps_json.sd ?? "—"}
                    </td>
                    <td>
                      <StatusDot
                        tone={channel.scte35_passthrough ? "ok" : "idle"}
                        label={channel.scte35_passthrough ? "pass" : "off"}
                      />
                    </td>
                    <td>
                      <Badge tone={channel.status === "active" ? "ok" : "idle"}>{channel.status}</Badge>
                    </td>
                    <td className="tabular-nums">{channel.endpoint_count ?? 0}</td>
                    <td className="text-right">
                      <button className="btn-ghost" onClick={() => setEditing(channel)}>
                        edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ChannelModal
          channel={editing === "new" ? null : editing}
          isAdmin={me.role === "nci_admin"}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
      {rewriting && <RewriteModal onClose={() => setRewriting(false)} onDone={refresh} />}
    </>
  );
}

function Origins({ urls }: { urls?: string[] }) {
  if (!urls?.length) return <span className="text-ink-500">—</span>;
  return (
    <div className="space-y-0.5 font-mono text-xs">
      {urls.map((url, index) => (
        <div key={url} className="flex items-baseline gap-2">
          <span className={index === 0 ? "text-ink-200" : "text-ink-400"}>{hostOf(url)}</span>
          <span className="text-[10px] uppercase text-ink-500">{index === 0 ? "primary" : "backup"}</span>
        </div>
      ))}
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function ChannelModal({
  channel,
  isAdmin,
  onClose,
  onDone,
}: {
  channel: Channel | null;
  isAdmin: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    number: channel?.number ?? 0,
    name: channel?.name ?? "",
    hd_primary: channel?.urls_json.hd?.[0] ?? "",
    hd_backup: channel?.urls_json.hd?.[1] ?? "",
    sd_primary: channel?.urls_json.sd?.[0] ?? "",
    sd_backup: channel?.urls_json.sd?.[1] ?? "",
    hd_kbps: channel?.nominal_kbps_json.hd ?? 2000,
    sd_kbps: channel?.nominal_kbps_json.sd ?? 900,
    scte35: channel?.scte35_passthrough ?? true,
    notes: channel?.notes ?? "",
  });
  const { run, busy } = useAction();

  const body = () => ({
    number: Number(form.number),
    name: form.name,
    urls_json: {
      hd: [form.hd_primary, form.hd_backup].filter(Boolean),
      sd: [form.sd_primary, form.sd_backup].filter(Boolean),
    },
    nominal_kbps_json: { hd: Number(form.hd_kbps), sd: Number(form.sd_kbps) },
    scte35_passthrough: form.scte35,
    notes: form.notes || null,
  });

  return (
    <Modal
      title={channel ? `Edit ${channel.name}` : "New channel"}
      onClose={onClose}
      wide
      footer={
        <>
          {channel && isAdmin && channel.status === "active" && (
            <ConfirmButton
              danger
              label="Retire"
              confirmLabel={`Retire — click again${channel.endpoint_count ? ` (${channel.endpoint_count} Outposts carry it)` : ""}`}
              onConfirm={() =>
                run(async () => {
                  await api.post(`/v1/channels/${channel.id}/retire?force=true`);
                  onDone();
                }, "Channel retired")
              }
            />
          )}
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !form.name || !form.number}
            onClick={() =>
              run(async () => {
                if (channel) await api.patch(`/v1/channels/${channel.id}`, body());
                else await api.post("/v1/channels", body());
                onDone();
              }, channel ? "Channel updated" : "Channel created")
            }
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-[6rem_1fr] gap-4">
          <Field label="Number">
            <input
              className="input"
              type="number"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: Number(e.target.value) })}
            />
          </Field>
          <Field label="Name">
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
        </div>

        <div className="rounded-md border border-ink-600 p-4">
          <div className="mb-3 text-sm font-medium">hd profile</div>
          <div className="space-y-3">
            <Field label="Primary DVR playlist">
              <input
                className="input font-mono text-xs"
                placeholder="https://dvr2.net-com.tv/WEAR_hd/index.m3u8"
                value={form.hd_primary}
                onChange={(e) => setForm({ ...form, hd_primary: e.target.value })}
              />
            </Field>
            <Field label="Backup DVR playlist">
              <input
                className="input font-mono text-xs"
                placeholder="https://dvr4.net-com.tv/WEAR_hd/index.m3u8"
                value={form.hd_backup}
                onChange={(e) => setForm({ ...form, hd_backup: e.target.value })}
              />
            </Field>
            <Field label="Nominal kbps" hint="From the transcoder ladder. Drives the reseller bandwidth estimate.">
              <input
                className="input"
                type="number"
                value={form.hd_kbps}
                onChange={(e) => setForm({ ...form, hd_kbps: Number(e.target.value) })}
              />
            </Field>
          </div>
        </div>

        <div className="rounded-md border border-ink-600 p-4">
          <div className="mb-3 text-sm font-medium">sd profile</div>
          <div className="space-y-3">
            <Field label="Primary DVR playlist">
              <input
                className="input font-mono text-xs"
                value={form.sd_primary}
                onChange={(e) => setForm({ ...form, sd_primary: e.target.value })}
              />
            </Field>
            <Field label="Backup DVR playlist">
              <input
                className="input font-mono text-xs"
                value={form.sd_backup}
                onChange={(e) => setForm({ ...form, sd_backup: e.target.value })}
              />
            </Field>
            <Field label="Nominal kbps">
              <input
                className="input"
                type="number"
                value={form.sd_kbps}
                onChange={(e) => setForm({ ...form, sd_kbps: Number(e.target.value) })}
              />
            </Field>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-brand"
            checked={form.scte35}
            onChange={(e) => setForm({ ...form, scte35: e.target.checked })}
          />
          Pass SCTE-35 markers through to the QAM output
        </label>

        <p className="text-xs text-ink-400">
          Point these at the <strong>profile-specific</strong> playlist, never the master — an Outpost pointed
          at a master would pull both profiles and double its WAN usage.
        </p>
      </div>
    </Modal>
  );
}

interface RewriteResponse {
  dry_run: boolean;
  matched: number;
  changes: { channel_id: number; number: number; name: string; profile: string; before: string; after: string }[];
  endpoints_affected: number;
  config_versions_created: number;
}

function RewriteModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [pattern, setPattern] = useState("dvr\\d+\\.net-com\\.tv");
  const [replacement, setReplacement] = useState("rs1.net-com.tv");
  const [preview, setPreview] = useState<RewriteResponse | null>(null);
  const { run, busy } = useAction();

  const call = (dryRun: boolean) =>
    run(async () => {
      const result = await api.post<RewriteResponse>("/v1/channels/bulk_rewrite", {
        pattern,
        replacement,
        regex: true,
        dry_run: dryRun,
      });
      setPreview(result);
      if (!dryRun) onDone();
    }, dryRun ? undefined : "Rewrite applied — Outposts pick it up within 30 seconds");

  return (
    <Modal
      title="Bulk URL rewrite"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => call(true)}>
            Preview
          </button>
          <button
            className="btn-primary"
            disabled={busy || !preview || preview.matched === 0}
            onClick={() => call(false)}
          >
            Apply to {preview?.matched ?? 0} URL{preview?.matched === 1 ? "" : "s"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border border-brand/30 bg-brand/10 p-3 text-xs text-ink-200">
          This is also how the restreamer tier is switched on: rewrite{" "}
          <code className="font-mono">dvrN.net-com.tv</code> to{" "}
          <code className="font-mono">rs1.net-com.tv</code> and the whole fleet moves on its next check-in,
          with no endpoint software change. The reverse operation rolls it back.
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Pattern (regular expression)">
            <input className="input font-mono text-xs" value={pattern} onChange={(e) => setPattern(e.target.value)} />
          </Field>
          <Field label="Replacement">
            <input
              className="input font-mono text-xs"
              value={replacement}
              onChange={(e) => setReplacement(e.target.value)}
            />
          </Field>
        </div>

        {preview && (
          <div>
            <div className="mb-2 text-sm">
              {preview.matched} URL{preview.matched === 1 ? "" : "s"} match ·{" "}
              {preview.endpoints_affected} Outpost{preview.endpoints_affected === 1 ? "" : "s"} affected
              {preview.config_versions_created > 0 && ` · ${preview.config_versions_created} configs reissued`}
            </div>
            <div className="max-h-64 overflow-y-auto rounded border border-ink-600">
              <table className="table text-xs">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Profile</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.changes.map((change, index) => (
                    <tr key={index}>
                      <td>
                        {change.number} · {change.name}
                      </td>
                      <td className="uppercase">{change.profile}</td>
                      <td className="font-mono text-ink-400">{hostOf(change.before)}</td>
                      <td className="font-mono text-brand-on">{hostOf(change.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
